import { useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { AlertTriangle, Info, X } from "lucide-react";
import "./PageModal.css";

export default function PageModal({
  open,
  onClose,
  onConfirm,
  title,
  description,
  children,
  confirmLabel = "Confirm",
  cancelLabel = "Cancel",
  tone = "default",
  icon = "warning",
  loading = false,
}) {
  useEffect(() => {
    if (!open) return undefined;

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    const onKeyDown = (event) => {
      if (event.key === "Escape" && !loading) {
        onClose();
      }
    };

    window.addEventListener("keydown", onKeyDown);

    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener("keydown", onKeyDown);
    };
  }, [open, loading, onClose]);

  const overlayRef = useRef(null);
  const mouseDownOnOverlay = useRef(false);

  if (!open) return null;

  const modalIcon = icon === "info" ? <Info size={18} /> : <AlertTriangle size={18} />;

  const handleOverlayMouseDown = (e) => {
    mouseDownOnOverlay.current = e.target === overlayRef.current;
  };

  const handleOverlayMouseUp = (e) => {
    if (!loading && mouseDownOnOverlay.current && e.target === overlayRef.current) {
      onClose();
    }
    mouseDownOnOverlay.current = false;
  };

  const modal = (
    <div
      className="page-modal-overlay"
      ref={overlayRef}
      onMouseDown={handleOverlayMouseDown}
      onMouseUp={handleOverlayMouseUp}
      role="presentation"
    >
      <div
        className={`page-modal page-modal-${tone}`}
        role="dialog"
        aria-modal="true"
        aria-labelledby="page-modal-title"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="page-modal-header">
          <div className={`page-modal-icon page-modal-icon-${tone} page-modal-icon-${icon}`}>
            {modalIcon}
          </div>
          <button
            type="button"
            className="page-modal-close"
            onClick={onClose}
            disabled={loading}
            aria-label="Close dialog"
          >
            <X size={16} />
          </button>
        </div>

        <div className="page-modal-body">
          <h3 id="page-modal-title">{title}</h3>
          {description && <p>{description}</p>}
          {children}
        </div>

        <div className="page-modal-actions">
          <button
            type="button"
            className="page-modal-button page-modal-button-secondary"
            onClick={onClose}
            disabled={loading}
          >
            {cancelLabel}
          </button>
          <button
            type="button"
            className={`page-modal-button page-modal-button-primary page-modal-button-${tone}`}
            onClick={onConfirm}
            disabled={loading}
          >
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );

  if (typeof document === "undefined") return modal;
  return createPortal(modal, document.body);
}
